import React from 'react';

import { Dialog, FormField, FormValidationMessage, SelectCustomizable, TextArea, UploadArea, UploadFileItem } from 'ui';

import FeedbackStatusModal from './FeedbackStatusModal';
import { ACCEPTED_IMAGE_EXTENSIONS, MAX_IMAGE_BYTES } from './useFeedbackAttachments';
import useFeedbackModal, { COMMENT_MAX_LENGTH } from './useFeedbackModal';

import './_feedback-modal.scss';

/**
 * FeedbackModal - Form where teachers and students share product feedback:
 * a category dropdown plus a free-text comment. The outcome of the submission
 * is surfaced in a follow-up status dialog.
 */

const CATEGORY_INPUT_ID = 'feedback-category';

type FeedbackModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

function FeedbackModal({ isOpen, onClose }: Readonly<FeedbackModalProps>): JSX.Element {
  const {
    attachments,
    attachmentError,
    attachLabel,
    attachmentHint,
    attachmentPrivacy,
    attachmentErrorRef,
    attachButtonRef,
    handleFilesSelected,
    handleRemoveAttachment,
    isAttachDisabled,
    uploadProgress,
    uploadStatus,
    cancelLabel,
    categoryLabel,
    categoryOptions,
    comment,
    commentLabel,
    commentPlaceholder,
    handleCategoryChange,
    handleClose,
    handleCommentChange,
    handleStatusClose,
    handleSubmit,
    isFormVisible,
    isSubmitDisabled,
    isSubmitting,
    selectedOption,
    status,
    statusDescription,
    statusTitle,
    submitLabel,
    subtitle,
    title,
  } = useFeedbackModal({ isOpen, onClose });

  return (
    <>
      <Dialog
        buttonTextCancel={cancelLabel}
        buttonTextSuccess={submitLabel}
        className="feedback-modal"
        hasSeparator
        isDisabledSuccess={isSubmitDisabled}
        isOpen={isOpen && isFormVisible}
        onCancel={handleClose}
        onClose={handleClose}
        onSuccess={handleSubmit}
        size="m"
        title={title}
      >
        <div className="feedback-modal__content">
          <p className="feedback-modal__subtitle">{subtitle}</p>

          <div className="feedback-modal__field">
            <SelectCustomizable
              inputId={CATEGORY_INPUT_ID}
              isDisabled={isSubmitting}
              isSearchable={false}
              label={categoryLabel}
              onChange={handleCategoryChange}
              options={categoryOptions}
              value={selectedOption}
            />
          </div>

          <div className="feedback-modal__comment">
            <div className="feedback-modal__comment-field">
              <FormField className="feedback-modal__field" isDisabled={isSubmitting} label={commentLabel}>
                <TextArea
                  disabled={isSubmitting}
                  maxLength={COMMENT_MAX_LENGTH}
                  onChange={handleCommentChange}
                  placeholder={commentPlaceholder}
                  rows={4}
                  value={comment}
                />
              </FormField>
              <UploadArea
                acceptedExtensions={ACCEPTED_IMAGE_EXTENSIONS}
                buttonRef={attachButtonRef}
                buttonText={attachLabel}
                className="feedback-modal__comment-footer"
                isDisabled={isSubmitting}
                isMultiple={false}
                isAtCapacity={isAttachDisabled}
                maxSizeBytes={MAX_IMAGE_BYTES}
                onFilesSelected={handleFilesSelected}
                variant="button"
              />
            </div>
            <div className="feedback-modal__attachments">
              <p className="feedback-modal__hint">{attachmentHint}</p>
              {attachments.length > 0 && (
                <ul className="feedback-modal__attachment-list">
                  {attachments.map((file) => (
                    <UploadFileItem key={file.name} file={file} isUploading={isSubmitting} onRemove={handleRemoveAttachment} progress={uploadProgress} />
                  ))}
                </ul>
              )}
              {attachmentError && (
                <div className="feedback-modal__attachment-error" ref={attachmentErrorRef} tabIndex={-1}>
                  <FormValidationMessage text={attachmentError} type="error" />
                </div>
              )}
              <p className="feedback-modal__hint">{attachmentPrivacy}</p>
              <output className="sr-only">{uploadStatus}</output>
            </div>
          </div>
        </div>
      </Dialog>

      <FeedbackStatusModal
        description={statusDescription}
        isOpen={isOpen && status !== null}
        onClose={handleStatusClose}
        title={statusTitle}
        variant={status === 'error' ? 'error' : 'success'}
      />
    </>
  );
}

export default FeedbackModal;
